<template>
  <div
    class="max-w-5xl mx-auto p-6 tenant-details"
    :class="{ 'dark-theme': isDarkTheme }"
  >
    <!-- Breadcrumb -->
    <nav class="mb-6">
      <router-link
        to="/platform/tenants"
        class="inline-flex items-center gap-2 text-orange-500
               hover:text-pink-500 transition-colors"
      >
        <i class="pi pi-arrow-left"></i>
        Back to Tenants
      </router-link>
    </nav>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center gap-3 py-16 text-orange-500">
      <ProgressSpinner style="width: 24px; height: 24px" strokeWidth="4" />
      <span>Loading tenant details...</span>
    </div>

    <template v-else-if="tenant">
      <!-- Header -->
      <div class="flex flex-wrap justify-between items-center gap-4 mb-8">
        <div class="flex items-center gap-4">
          <div
            class="w-16 h-16 rounded-xl flex items-center justify-center
                   text-white font-bold text-xl"
            :style="{ backgroundColor: getTenantColor(tenant.slug) }"
          >
            {{ getInitials(tenant.name) }}
          </div>
          <div>
            <h1 class="text-2xl font-bold text-gray-800">{{ tenant.name }}</h1>
            <div class="flex flex-wrap gap-2 mt-2">
              <Tag severity="secondary">{{ tenant.slug }}</Tag>
              <Tag :severity="statusSeverity">{{ tenant.status }}</Tag>
              <Tag :severity="planSeverity">{{ tenant.plan || 'standard' }}</Tag>
            </div>
          </div>
        </div>
        <div class="flex gap-3">
          <Button
            :label="exporting ? 'Exporting...' : 'Export'"
            :icon="exporting ? 'pi pi-spin pi-spinner' : 'pi pi-download'"
            :disabled="exporting"
            severity="info"
            @click="exportTenant"
          />
          <Button
            label="Access Tenant"
            icon="pi pi-external-link"
            severity="success"
            @click="accessTenant"
          />
          <Button
            label="Edit"
            icon="pi pi-pencil"
            severity="secondary"
            outlined
            @click="editTenant"
          />
        </div>
      </div>

      <!-- Stats Grid -->
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card class="bg-gradient-to-br from-blue-50 to-indigo-50 border-none">
          <template #content>
            <div class="flex items-center gap-3">
              <div class="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
                <i class="pi pi-users text-white text-xl"></i>
              </div>
              <div>
                <p class="text-2xl font-bold text-gray-800">{{ tenant.userCount || 0 }}</p>
                <span class="text-sm text-gray-500">Total Users</span>
              </div>
            </div>
          </template>
        </Card>

        <Card class="bg-gradient-to-br from-green-50 to-emerald-50 border-none">
          <template #content>
            <div class="flex items-center gap-3">
              <div class="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center">
                <i class="pi pi-file text-white text-xl"></i>
              </div>
              <div>
                <p class="text-2xl font-bold text-gray-800">{{ tenant.assessmentCount || 0 }}</p>
                <span class="text-sm text-gray-500">Assessments</span>
              </div>
            </div>
          </template>
        </Card>

        <Card class="bg-gradient-to-br from-purple-50 to-pink-50 border-none">
          <template #content>
            <div class="flex items-center gap-3">
              <div class="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center">
                <i class="pi pi-database text-white text-xl"></i>
              </div>
              <div>
                <p class="text-2xl font-bold text-gray-800">{{ formatSize(tenant.dbSize) }}</p>
                <span class="text-sm text-gray-500">Database Size</span>
              </div>
            </div>
          </template>
        </Card>

        <Card class="bg-gradient-to-br from-orange-50 to-amber-50 border-none">
          <template #content>
            <div class="flex items-center gap-3">
              <div class="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center">
                <i class="pi pi-calendar text-white text-xl"></i>
              </div>
              <div>
                <p class="text-2xl font-bold text-gray-800">{{ formatDate(tenant.createdAt) }}</p>
                <span class="text-sm text-gray-500">Created</span>
              </div>
            </div>
          </template>
        </Card>
      </div>

      <!-- Details Sections -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <!-- Database Info -->
        <Card>
          <template #title>
            <div class="flex items-center gap-2 text-gray-800">
              <i class="pi pi-database text-orange-500"></i>
              Database Configuration
            </div>
          </template>
          <template #content>
            <div class="space-y-3">
              <div class="flex justify-between items-center py-2 border-b border-gray-100">
                <span class="text-gray-500">Database Name</span>
                <Tag severity="secondary">{{ tenant.dbName }}</Tag>
              </div>
              <div class="flex justify-between items-center py-2 border-b border-gray-100">
                <span class="text-gray-500">Status</span>
                <div class="flex items-center gap-2">
                  <span
                    class="w-2 h-2 rounded-full"
                    :class="{
                      'bg-green-500': tenant.status === 'active',
                      'bg-yellow-500': tenant.status === 'provisioning',
                      'bg-red-500': tenant.status === 'suspended'
                    }"
                  ></span>
                  {{ tenant.status }}
                </div>
              </div>
              <div class="flex justify-between items-center py-2 border-b border-gray-100">
                <span class="text-gray-500">Created</span>
                <span class="text-gray-800">{{ formatDateTime(tenant.createdAt) }}</span>
              </div>
              <div class="flex justify-between items-center py-2">
                <span class="text-gray-500">Last Updated</span>
                <span class="text-gray-800">{{ formatDateTime(tenant.updatedAt) }}</span>
              </div>
            </div>
          </template>
        </Card>

        <!-- Contact Info -->
        <Card>
          <template #title>
            <div class="flex items-center gap-2 text-gray-800">
              <i class="pi pi-user text-orange-500"></i>
              Contact Information
            </div>
          </template>
          <template #content>
            <div class="space-y-3">
              <div class="flex justify-between items-center py-2 border-b border-gray-100">
                <span class="text-gray-500">Contact Name</span>
                <span class="text-gray-800">{{ tenant.contactName || '-' }}</span>
              </div>
              <div class="flex justify-between items-center py-2 border-b border-gray-100">
                <span class="text-gray-500">Contact Email</span>
                <span class="text-gray-800">{{ tenant.contactEmail || '-' }}</span>
              </div>
              <div class="flex justify-between items-center py-2">
                <span class="text-gray-500">Max Users</span>
                <span class="text-gray-800">{{ tenant.maxUsers || 'Unlimited' }}</span>
              </div>
            </div>
          </template>
        </Card>

        <!-- Features -->
        <Card class="lg:col-span-2">
          <template #title>
            <div class="flex items-center gap-2 text-gray-800">
              <i class="pi pi-star text-orange-500"></i>
              Enabled Features
            </div>
          </template>
          <template #content>
            <div class="flex flex-wrap gap-2">
              <Chip
                v-for="feature in tenant.features"
                :key="feature"
                :label="formatFeatureName(feature)"
                :icon="getFeatureIcon(feature)"
                class="bg-orange-100 text-orange-800"
              />
              <span v-if="!tenant.features?.length" class="text-gray-400">No features enabled</span>
            </div>
          </template>
        </Card>

        <!-- Description -->
        <Card v-if="tenant.description" class="lg:col-span-2">
          <template #title>
            <div class="flex items-center gap-2 text-gray-800">
              <i class="pi pi-info-circle text-orange-500"></i>
              Description
            </div>
          </template>
          <template #content>
            <p class="text-gray-600 leading-relaxed">{{ tenant.description }}</p>
          </template>
        </Card>
      </div>

      <TenantMigrationsSection
        v-if="tenant.status !== 'deleted'"
        :tenant-slug="tenantSlug"
      />

      <!-- Danger Zone -->
      <Card class="border-red-200 bg-red-50/50">
        <template #title>
          <div class="flex items-center gap-2 text-red-600">
            <i class="pi pi-exclamation-triangle"></i>
            Danger Zone
          </div>
        </template>
        <template #content>
          <div class="space-y-4">
            <div class="flex justify-between items-center p-4 bg-white
                        rounded-xl border border-red-100">
              <div>
                <h4 class="font-semibold text-gray-800">Suspend Tenant</h4>
                <p class="text-sm text-gray-500">Temporarily disable access for all users</p>
              </div>
              <Button
                v-if="tenant.status !== 'suspended'"
                label="Suspend"
                severity="warning"
                outlined
                @click="confirmSuspend"
              />
              <Button
                v-else
                label="Activate"
                severity="success"
                outlined
                @click="confirmActivate"
              />
            </div>
            <div class="flex justify-between items-center p-4 bg-white
                        rounded-xl border border-red-100">
              <div>
                <h4 class="font-semibold text-gray-800">Delete Tenant</h4>
                <p class="text-sm text-gray-500">Permanently delete this tenant and all data</p>
              </div>
              <Button
                label="Delete"
                severity="danger"
                outlined
                @click="confirmDelete"
              />
            </div>
          </div>
        </template>
      </Card>
    </template>

    <!-- Not Found -->
    <div v-else class="flex flex-col items-center justify-center py-16 text-center">
      <i class="pi pi-exclamation-circle text-5xl text-yellow-500 mb-4"></i>
      <h2 class="text-xl font-semibold text-gray-800 mb-2">Tenant Not Found</h2>
      <p class="text-gray-500 mb-6">The tenant you're looking for doesn't exist.</p>
      <Button
        label="Back to Tenants"
        icon="pi pi-arrow-left"
        @click="$router.push('/platform/tenants')"
      />
    </div>

    <!-- Confirm Dialog -->
    <ConfirmDialog />
  </div>
</template>

<script>
import { platformApi } from '@/services/platformApi';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Chip from 'primevue/chip';
import ProgressSpinner from 'primevue/progressspinner';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import TenantMigrationsSection from './TenantMigrationsSection.vue';

export default {
  name: 'TenantDetails',
  components: {
    Card,
    Button,
    Tag,
    Chip,
    ProgressSpinner,
    ConfirmDialog,
    TenantMigrationsSection,
  },
  props: {
    isDarkTheme: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const confirm = useConfirm();
    return { confirm };
  },
  data() {
    return {
      loading: true,
      exporting: false,
      tenant: null,
    };
  },
  computed: {
    tenantSlug() {
      return this.$route.params.slug;
    },
    statusSeverity() {
      const map = {
        active: 'success',
        provisioning: 'warn',
        suspended: 'danger',
      };
      return map[this.tenant?.status] || 'secondary';
    },
    planSeverity() {
      const map = {
        free: 'secondary',
        standard: 'info',
        premium: 'warn',
        enterprise: 'success',
      };
      return map[this.tenant?.plan] || 'secondary';
    },
  },
  mounted() {
    this.loadTenant();
  },
  methods: {
    async loadTenant() {
      this.loading = true;
      try {
        const tenant = await platformApi.getTenant(this.tenantSlug);
        this.tenant = tenant || null;
      } catch (error) {
        console.error('Failed to load tenant:', error);
        this.tenant = null;
      } finally {
        this.loading = false;
      }
    },
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map((w) => w[0]).join('')
        .toUpperCase()
        .slice(0, 2);
    },
    getTenantColor(slug) {
      const colors = [
        '#e4821a', '#ec4899', '#3b82f6', '#22c55e',
        '#f59e0b', '#ef4444', '#06b6d4', '#10b981',
      ];
      let hash = 0;
      for (let i = 0; i < slug.length; i += 1) {
        // eslint-disable-next-line no-bitwise
        hash = slug.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    },
    formatDateTime(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },
    formatSize(bytes) {
      if (!bytes) return '0 MB';
      const mb = bytes / (1024 * 1024);
      return mb < 1 ? `${(mb * 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
    },
    getFeatureIcon(feature) {
      const icons = {
        assessments: 'pi pi-file',
        devtips: 'pi pi-lightbulb',
        dashboard: 'pi pi-chart-bar',
        kss: 'pi pi-calendar',
        mindset360: 'pi pi-refresh',
        levelSystem: 'pi pi-star',
        travelBuddy: 'pi pi-users',
        workshop: 'pi pi-video',
      };
      return icons[feature] || 'pi pi-check';
    },
    formatFeatureName(feature) {
      return feature.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
    },
    editTenant() {
      this.$router.push(`/platform/tenants/${this.tenantSlug}/edit`);
    },
    accessTenant() {
      const { hostname, port, protocol } = window.location;
      const baseDomain = hostname.replace(/^admin\./, '');
      const isStandardPort = !port || port === '80' || port === '443';
      const tenantPort = isStandardPort ? '' : `:${port}`;
      const tenantUrl = `${protocol}//${this.tenant.slug}.${baseDomain}${tenantPort}`;
      window.open(tenantUrl, '_blank');
    },
    async exportTenant() {
      this.exporting = true;
      try {
        // downloadTenantExport handles the file download internally
        await platformApi.downloadTenantExport(this.tenantSlug);

        this.$toast.add({
          severity: 'success',
          summary: 'Export Complete',
          detail: 'Tenant data exported successfully',
          life: 3000,
        });
      } catch (error) {
        console.error('Failed to export tenant:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Export Failed',
          detail: 'Failed to export tenant. Please try again.',
          life: 5000,
        });
      } finally {
        this.exporting = false;
      }
    },
    confirmSuspend() {
      this.confirm.require({
        message: `Are you sure you want to suspend "${this.tenant.name}"?`,
        header: 'Suspend Tenant',
        icon: 'pi pi-exclamation-triangle',
        rejectLabel: 'Cancel',
        acceptLabel: 'Suspend',
        acceptClass: 'p-button-warning',
        accept: () => this.suspendTenant(),
      });
    },
    confirmActivate() {
      this.confirm.require({
        message: `Are you sure you want to activate "${this.tenant.name}"?`,
        header: 'Activate Tenant',
        icon: 'pi pi-check-circle',
        rejectLabel: 'Cancel',
        acceptLabel: 'Activate',
        acceptClass: 'p-button-success',
        accept: () => this.activateTenant(),
      });
    },
    confirmDelete() {
      this.confirm.require({
        message: `Are you sure you want to DELETE "${this.tenant.name}"? This will permanently delete all data.`,
        header: 'Delete Tenant',
        icon: 'pi pi-trash',
        rejectLabel: 'Cancel',
        acceptLabel: 'Delete Permanently',
        acceptClass: 'p-button-danger',
        accept: () => this.deleteTenant(),
      });
    },
    async suspendTenant() {
      try {
        await platformApi.suspendTenant(this.tenantSlug);
        this.$toast.add({ severity: 'success', summary: 'Tenant Suspended', life: 3000 });
        this.loadTenant();
      } catch (error) {
        console.error('Failed to suspend tenant:', error);
        this.$toast.add({ severity: 'error', summary: 'Failed to suspend tenant', life: 5000 });
      }
    },
    async activateTenant() {
      try {
        await platformApi.activateTenant(this.tenantSlug);
        this.$toast.add({ severity: 'success', summary: 'Tenant Activated', life: 3000 });
        this.loadTenant();
      } catch (error) {
        console.error('Failed to activate tenant:', error);
        this.$toast.add({ severity: 'error', summary: 'Failed to activate tenant', life: 5000 });
      }
    },
    async deleteTenant() {
      try {
        await platformApi.deleteTenant(this.tenantSlug);
        this.$toast.add({ severity: 'success', summary: 'Tenant Deleted', life: 3000 });
        this.$router.push('/platform/tenants');
      } catch (error) {
        console.error('Failed to delete tenant:', error);
        this.$toast.add({ severity: 'error', summary: 'Failed to delete tenant', life: 5000 });
      }
    },
  },
};
</script>

<style scoped>
/* Dark Theme Overrides */
.tenant-details.dark-theme {
  color: #f5f5f5;
}

.tenant-details.dark-theme .text-gray-800 {
  color: #f5f5f5 !important;
}

.tenant-details.dark-theme .text-gray-700 {
  color: #e0e0e0 !important;
}

.tenant-details.dark-theme .text-gray-600 {
  color: #c0c0c0 !important;
}

.tenant-details.dark-theme .text-gray-500 {
  color: #a0a0a0 !important;
}

.tenant-details.dark-theme .text-gray-400 {
  color: #909090 !important;
}

.tenant-details.dark-theme .bg-white {
  background-color: #2a2a24 !important;
}

.tenant-details.dark-theme .bg-gray-50 {
  background-color: #1E2019 !important;
}

.tenant-details.dark-theme .border-gray-100,
.tenant-details.dark-theme .border-gray-200 {
  border-color: rgba(228, 130, 26, 0.2) !important;
}

.tenant-details.dark-theme .border-red-100,
.tenant-details.dark-theme .border-red-200 {
  border-color: rgba(239, 68, 68, 0.3) !important;
}

.tenant-details.dark-theme .bg-red-50\/50 {
  background-color: rgba(239, 68, 68, 0.1) !important;
}

/* PrimeVue Card dark overrides */
.tenant-details.dark-theme :deep(.p-card) {
  background: #2a2a24;
  color: #f5f5f5;
  border: 1px solid rgba(228, 130, 26, 0.2);
}

.tenant-details.dark-theme :deep(.p-card .p-card-title) {
  color: #f5f5f5;
}

.tenant-details.dark-theme :deep(.p-card .p-card-content) {
  color: #e0e0e0;
}

/* Gradient backgrounds stay the same but with darker base */
.tenant-details.dark-theme .from-blue-50,
.tenant-details.dark-theme .from-green-50,
.tenant-details.dark-theme .from-purple-50,
.tenant-details.dark-theme .from-orange-50 {
  --tw-gradient-from: rgba(59, 130, 246, 0.15) !important;
}

.tenant-details.dark-theme .to-indigo-50,
.tenant-details.dark-theme .to-emerald-50,
.tenant-details.dark-theme .to-pink-50,
.tenant-details.dark-theme .to-amber-50 {
  --tw-gradient-to: rgba(99, 102, 241, 0.1) !important;
}
</style>
